import { Stepper } from '@biq/apex-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, StepProps } from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
    isAttributeAssociationNonEmpty,
    updateEmptyParameterInputErrors,
} from '../../../../components/paired-input-selection-widget/paired-input-selection-helper';
import { JOB_MANAGER_PATHS } from '../../../../constants/job-manager-paths';
import { useAuth, usePermission } from '../../../../core';
import { useExecuteSmartContract } from '../../../../hooks';
import { SmartContract, validationsMessages } from '../../../../lib';
import { AttributeAssociationItem } from '../../../edge-definitions/edge-definition-helper';
import { CreateJobDialog } from '../create-job-dialog';
import {
    executeSmartContractJobDefaults,
    ExecuteSmartContractJobFormInputs,
    ExecuteSmartContractJobSchema,
    executeSmartContractJobStyles,
    executeSmartContractSteps,
} from './create-execute-smart-contract-job-helper';
import { BasicInfoForm } from './BasicInfoForm';
import { ExecuteSmartContractForm } from './ExecuteSmartContractForm';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';

export const CreateExecuteSmartContractJob = (): React.JSX.Element => {
    const navigate = useNavigate();
    const { getToken } = useAuth();
    const { hasPermissions } = usePermission();

    const {
        control,
        formState: { errors },
        setValue,
        clearErrors,
        setError,
        watch,
    } = useForm<ExecuteSmartContractJobFormInputs>({
        mode: 'all',
        resolver: yupResolver(ExecuteSmartContractJobSchema),
        defaultValues: executeSmartContractJobDefaults,
    });

    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [selectedChain, setSelectedChain] = useState<string>('');
    const [selectedSmartContract, setSelectedSmartContract] =
        useState<SmartContract | null>(null);
    const [selectedFunction, setSelectedFunction] = useState<string>('');
    const [attributeAssociationList, setAttributeAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState<any[]>(
        executeSmartContractSteps.map((stepItem: StepProps, index: number) => {
            return {
                ...stepItem,
                state: index === 0 ? 'active' : 'inactive',
            };
        })
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { mutate: executeSmartContract } = useExecuteSmartContract();

    const handleExecuteSmartContractJob = (): void => {
        setIsLoading(true);
        setTimeout(() => {
            navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
        }, 500);
        executeSmartContract(
            {
                token: getToken(),
                chain: selectedChain,
                sdoId: selectedSmartContract?.header?.id ?? '',
                func: selectedFunction,
                args: attributeAssociationList,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const clearParamListErrors = (): void => {
        setAttributeAssociationList(
            attributeAssociationList.map((item) => ({
                ...item,
                leftSideError: false,
                rightSideError: false,
            }))
        );
    };

    const getActiveStep = (): number => activeStep;

    const handleSelectedChain = (chain: string): void => {
        setValue('chain', chain);
        setSelectedChain(chain);
        clearErrors('chain');
        clearErrors('func');
        setValue('smartContract', null);
        setSelectedSmartContract(null);
    };

    const handleSelectedSmartContract = (
        smartContract: SmartContract
    ): void => {
        setValue('smartContract', smartContract);
        setSelectedSmartContract(smartContract);
        clearErrors('smartContract');
        clearErrors('func');
    };

    const handleFunctionChange = (value: string): void => {
        setValue('func', value);
        setSelectedFunction(value);
        clearErrors('func');
    };

    const onAttributeAssociationsChanged = (
        list: AttributeAssociationItem[]
    ): void => {
        setAttributeAssociationList(list);
    };

    const handleState = (button: string): void => {
        setSteps((prevStep: any) =>
            prevStep.map((item: { state: string }, index: number) => {
                if (index === activeStep) {
                    item.state = button === 'next' ? 'complete' : 'inactive';
                } else if (index === activeStep + 1) {
                    item.state = 'active';
                } else if (index < activeStep) {
                    item.state = 'complete';
                } else {
                    item.state = 'inactive';
                }
                return item;
            })
        );
    };

    const handleStateForBack = (): void => {
        setSteps((prevStep) =>
            prevStep.map((item, index) => {
                if (index === activeStep) {
                    item.state = 'inactive';
                } else if (index === activeStep - 1) {
                    item.state = 'active';
                }
                return item;
            })
        );
    };

    const handleBack = (): void => {
        if (activeStep === 0) {
            navigate(JOB_MANAGER_PATHS.CREATE_NEW_JOB);
        } else {
            clearParamListErrors();
            clearErrors('func');
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
            handleStateForBack();
        }
    };

    const handleNext = (): void => {
        if (activeStep === steps.length - 1) {
            setSteps((prevStep) =>
                prevStep.map((item, index) => {
                    if (index === activeStep) {
                        item.state = 'complete';
                    }
                    return item;
                })
            );
            return;
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        handleState('next');
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleNextClick = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            if (!selectedChain) setError('chain', { type: 'required' });
            if (!selectedSmartContract)
                setError('smartContract', { type: 'required' });
            if (selectedChain && selectedSmartContract) handleNext();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const validateNonEmptyItems = isAttributeAssociationNonEmpty(
        attributeAssociationList
    );

    const handleExecuteSmartContract = (): void => {
        if (!selectedChain) setError('chain', { type: 'required' });
        if (!selectedSmartContract)
            setError('smartContract', { type: 'required' });
        if (!selectedFunction) setError('func', { type: 'required' });
        if (!validateNonEmptyItems) {
            updateEmptyParameterInputErrors(
                attributeAssociationList,
                onAttributeAssociationsChanged,
                validationsMessages.required
            );
        }
        if (
            selectedChain &&
            selectedSmartContract &&
            selectedFunction &&
            validateNonEmptyItems
        ) {
            handleExecuteSmartContractJob();
        }
    };

    return (
        <CreateJobDialog
            handleCustomBackClick={handleBack}
            handleSubmit={handleNextClick}
            buttonText={'NEXT'}
            displaySubmitButton={activeStep === 0}
            customStyles={
                activeStep !== 0
                    ? executeSmartContractJobStyles.root
                    : undefined
            }
            isLoading={isLoading}
        >
            <Grid sx={executeSmartContractJobStyles.mainContainer}>
                <Grid sx={executeSmartContractJobStyles.stepperContainer}>
                    <Stepper
                        getActiveStep={getActiveStep}
                        nonLinear={false}
                        orientation='horizontal'
                        textAlignment='center'
                        steps={steps}
                        sx={executeSmartContractJobStyles.stepper}
                    />
                </Grid>
                {activeStep === 0 ? (
                    <BasicInfoForm
                        control={control}
                        errors={errors}
                        selectedChain={selectedChain}
                        selectedSmartContract={selectedSmartContract}
                        onChainChange={handleSelectedChain}
                        onSmartContractChange={handleSelectedSmartContract}
                        smartContractFieldValue={watch('smartContract')}
                    />
                ) : (
                    <ExecuteSmartContractForm
                        control={control}
                        errors={errors}
                        encodedQuery={selectedSmartContract?.codeBase64}
                        handleSubmit={handleExecuteSmartContract}
                        onFunctionChange={handleFunctionChange}
                        attributeAssociationList={attributeAssociationList}
                        handleAttributeAssociationsChanged={
                            onAttributeAssociationsChanged
                        }
                    />
                )}
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
